<template>
  <div class="make-payment-container">
    <div v-if="loadState === undefined" class="bill-pay__page-loader">
      <img src="/wp-content/themes/gmptwentynineteen/assets/images/loader-grey.svg" class="bill-pay__page-loader--svg" />
    </div>
    <flow-error v-if="loadState === 'error'" name="Bill Pay" state="error" phonetext="To make a payment by phone call us at" closepath="/make-payment" />
    <div v-if="loadState === 'complete'">
      <!-- SLAT 1 -->
      <section id="pay-slat-1" class="payment-amount bill-pay-section">
        <div class="bill-pay-slat gds-slat gds-slat--active">
          <div class="bill-pay-slat__header gds-slat__header">
            <div class="bill-pay-slat__header-left gds-flex-container">
              <div v-if="currentPage <= 1" class="bill-pay-slat__number-with-circle">
                <span>1</span>
              </div>
              <div v-if="currentPage > 1" class="bill-pay-slat__green-check-circle">
                <svg class="gds-icon">
                  <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-check" />
                </svg>
              </div>
              <div class="gds-slat__title-wrapper">
                <div class="bill-pay-slat__title gds-slat__title">Payment Amount</div>
              </div>
            </div>
            <div class="bill-pay-slat__header-right gds-flex-container">
              <div v-if="currentPage > 1" class="bill-pay-slat__header-summary gds-flex-container gds-flex-container">
                <div class="gds-body-normal gds-space-inline-m">Payment amount: {{paymentAmount | currency}}</div>
                <button @click="gotoPage(1)" class="bill-pay-slat__header-edit-button gds-button gds-round gds-secondary">Edit</button>
              </div>
              <div v-if="currentPage < 1" class="bill-pay-slat__lock-icon">
                <svg class="gds-icon">
                  <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-lock" />
                </svg>
              </div>
            </div>
          </div>
          <div v-if="currentPage === 1" class="bill-pay-slat__contents">
            <div class="bill-pay-slat__contents-inner">
              <div class="bill-pay-slat__inner-row gds-space-stack-l">
                <div class="bill-pay__amount-flex-container gds-flex-container gds-flex-container--top">
                  <div class="bill-pay__amount-column">
                    <div class="gds-font-demi gds-font-size-medium gds-space-stack-s">Current balance</div>
                    <div v-if="!pendingPayments">
                      <div v-if="amountDue > 0" class="gds-body-normal gds-space-stack-s">{{amountDue | currency}} is due {{currentAccount.amountDueDate | dateFull}}.</div>
                      <div v-if="amountDue <= 0" class="gds-body-normal">All paid up, you do not owe any money at this time.</div>
                      <div v-if="pastDue && amountDue > 0" class="gds-body-normal">Part of this balance is due immediately.</div>
                    </div>
                    <div v-if="pendingPayments">
                      <div v-if="amountDue > 0">
                        <div v-if="currentAccount.pendingPaymentsCount === 1" class="gds-body-normal gds-space-stack-s">There is 1 payment pending.</div>
                        <div
                          v-if="currentAccount.pendingPaymentsCount > 1"
                          class="gds-body-normal gds-space-stack-s"
                        >There are {{currentAccount.pendingPaymentsCount}} payments pending.</div>
                        <div class="gds-body-normal gds-space-stack-s">The remaining balance {{currentAccount.amountDueMinusPending > 0 ? 'due ' : ''}}is {{amountDue | currency}}.</div>
                      </div>
                      <div v-if="amountDue <= 0" class="gds-body-normal">You recently paid your balance in full. You do not owe any money at this time.</div>
                    </div>
                  </div>
                  <div class="bill-pay__amount-column">
                    <div class="gds-font-demi gds-font-size-medium gds-space-stack-s">Service Address</div>
                    <div class="gds-body-normal gds-linebreaks">{{currentAccount.address | addressFormat}}</div>
                  </div>
                  <div class="bill-pay__amount-column">
                    <div class="gds-font-demi gds-font-size-medium gds-space-stack-s">Account Number</div>
                    <div class="gds-body-normal">{{currentAccount.accountNumber}}</div>
                  </div>
                </div>
              </div>
              <div class="bill-pay-slap__inner-row gds-space-stack-l">
                <form data-vv-scope="page1" @submit.prevent>
                  <fieldset class="bill-pay__fieldset--wide-hint gds-fieldset gds-space-stack-s">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Payment amount</span>
                      <div class="gds-position--relative">
                        <span class="gds-input-field__prefix gds-text">$</span>
                        <money
                          v-model="paymentAmount"
                          v-validate="'required|min_value:1|max_value:9999999'"
                          name="payment amount"
                          key="payment amount"
                          class="bill-pay-input-field__input gds-input-field__input"
                          autofocus
                          ref="focuser1"
                        ></money>
                      </div>
                      <div v-if="errors.first('payment amount', 'page1')" class="validation-error">{{ errors.first("payment amount", "page1")}}</div>
                      <div
                        v-if="paymentAmount < 10"
                        class="form-input-hint form-input-hint--blue"
                      >Card payments have a $10.00 minimum. If you’d like to be able to pay using a credit, debit, or ATM card, please adjust the payment amount.</div>
                      <div
                        v-if="paymentAmount > 0 && paymentAmount > amountDue"
                        class="form-input-hint form-input-hint--blue"
                      >The payment amount you entered is more than what is due at this time. Any overpayment will be automatically credited on the next bill we issue you.</div>
                    </label>
                  </fieldset>
                </form>
                <button @click="finishPage('page1', 2)" class="gds-button gds-compact gds-space-stack-l">Next</button>
                <a href="/account/settings#autopay" class="gds-display-0 gds-link gds-link--bold">Setup or Manage Recurring Auto Payments</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- SLAT 2 -->
      <section id="pay-slat-2" class="payment-method bill-pay-section">
        <div class="bill-pay-slat gds-slat gds-slat--active">
          <div class="bill-pay-slat__header gds-slat__header">
            <div class="bill-pay-slat__header-left gds-flex-container">
              <div v-if="currentPage <= 2" class="bill-pay-slat__number-with-circle">
                <span>2</span>
              </div>
              <div v-if="currentPage > 2" class="bill-pay-slat__green-check-circle">
                <svg class="gds-icon">
                  <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-check" />
                </svg>
              </div>
              <div class="gds-slat__title-wrapper">
                <div class="bill-pay-slat__title gds-slat__title">Payment Method</div>
              </div>
            </div>
            <div class="bill-pay-slat__header-right gds-flex-container">
              <div v-if="currentPage > 2" class="bill-pay-slat__header-summary gds-flex-container gds-flex-container">
                <div class="gds-body-normal gds-space-inline-m">{{selectedWallet ? selectedWallet.accountNickName : ''}}</div>
                <button @click="gotoPage(2)" class="bill-pay-slat__header-edit-button gds-button gds-round gds-secondary">Edit</button>
              </div>
              <div v-if="currentPage < 2" class="bill-pay-slat__lock-icon">
                <svg class="gds-icon">
                  <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-lock" />
                </svg>
              </div>
            </div>
          </div>
          <div v-if="currentPage === 2" class="bill-pay-slat__contents">
            <div class="bill-pay-slat__contents-inner">
              <div
                v-if="tooManyCCPayments"
                class="form-message-box form-message-box--blue gds-space-stack-l"
              >Card payments are disabled because two or more such payments have been made in the last month. If you would like to make additional payments on your account this month, please use a bank account.</div>
              <div
                v-if="!tooManyCCPayments && paymentAmount < paymentMeta.minCC"
                class="form-message-box form-message-box--blue gds-space-stack-l"
              >Card payments are disabled because the payment amount is below the minimum of {{paymentMeta.minCC | currency}}. If you'd like to pay using a debit or credit card, please edit your payment amount.</div>
              <div
                v-if="!tooManyCCPayments && paymentAmount > paymentMeta.maxCC"
                class="form-message-box form-message-box--blue gds-space-stack-l"
              >Card payments are disabled because the payment amount is above the maximum of {{paymentMeta.maxCC | currency}}. If you'd like to pay using a debit or credit card, please edit your payment amount.</div>

              <div class="bill-pay-slat__inner-row gds-space-stack-l">
                <div class="gds-body-small gds-space-stack-l">Please select your payment method.</div>
                <form class="bill-pay-login__form" @submit.prevent role="radiogroup">
                  <fieldset class="gds-fieldset gds-space-inset-s">
                    <wallet-selector
                      v-for="wallet of userWallets"
                      :key="wallet.walletId"
                      :value="wallet"
                      v-model="selectedWallet"
                      :disabled="(wallet.paymentType === 'CC' || wallet.paymentType === 'ATM') && (tooManyCCPayments || paymentAmount < paymentMeta.minCC || paymentAmount > paymentMeta.maxCC)"
                      :isNew="wallet.isNew"
                    />
                  </fieldset>
                </form>
                <button @click="finishPage('page2', 3)" class="bill-pay__method-next-btn gds-button gds-compact">Next</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- SLAT 3 -->
      <section id="pay-slat-3" class="payment-details bill-pay-section">
        <div class="bill-pay-slat gds-slat gds-slat--active">
          <div class="bill-pay-slat__header gds-slat__header">
            <div class="bill-pay-slat__header-left gds-flex-container">
              <div v-if="currentPage <= 3" class="bill-pay-slat__number-with-circle">
                <span>3</span>
              </div>
              <div v-if="currentPage > 3" class="bill-pay-slat__green-check-circle">
                <svg class="gds-icon">
                  <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-check" />
                </svg>
              </div>
              <div class="gds-slat__title-wrapper">
                <div class="bill-pay-slat__title gds-slat__title">Payment Details</div>
              </div>
            </div>
            <div class="bill-pay-slat__header-right gds-flex-container">
              <div v-if="currentPage > 3" class="bill-pay-slat__header-summary gds-flex-container gds-flex-container">
                <div class="gds-body-normal gds-space-inline-m">{{selectedWallet | walletSlug}}</div>
                <button @click="gotoPage(3)" class="bill-pay-slat__header-edit-button gds-button gds-round gds-secondary">Edit</button>
              </div>
              <div v-if="currentPage < 3" class="bill-pay-slat__lock-icon">
                <svg class="gds-icon">
                  <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-lock" />
                </svg>
              </div>
            </div>
          </div>
          <div v-if="currentPage === 3" class="bill-pay-slat__contents">
            <div class="bill-pay-slat__contents-inner">
              <!-- SLAT 3 NEW CC INPUT -->
              <div v-if="selectedWallet && selectedWallet.isNew === 'credit'">
                <form data-vv-scope="page3NewCC" @submit.prevent>
                  <div class="bill-pay-slat__inner-row gds-space-stack-l">Please provide your card information.</div>
                  <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Name on Card</span>
                      <input
                        v-model="selectedWallet.debitName"
                        v-validate="'required|max:45'"
                        name="name on card"
                        key="name on card"
                        class="bill-pay-input-field__input gds-input-field__input"
                        type="text"
                        placeholder="Name"
                        maxlength="45"
                        autocomplete="cc-name"
                        ref="focuser3"
                      />
                      <div v-if="errors.first('name on card', 'page3NewCC')" class="validation-error">{{ errors.first("name on card", "page3NewCC")}}</div>
                    </label>
                  </fieldset>
                  <div class="bill-pay-slat__flex-container gds-flex-container gds-flex-container--top gds-space-stack-s">
                    <fieldset class="bill-pay-slat__card-number gds-fieldset">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Card Number</span>
                        <div class="gds-position--relative">
                          <span class="bill-pay__credit-card-prefix gds-input-field__prefix gds-text">
                            <svg class="bill-pay__icon gds-icon">
                              <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-credit-card" />
                            </svg>
                          </span>
                          <input
                            v-model="creditNumber"
                            v-mask="'################'"
                            v-validate="(selectedWallet.lastFourDigitDebitAccount ? '' : 'required|') + 'numeric|min:15|max:16'"
                            name="card number"
                            key="card number"
                            class="gds-input-field__input"
                            type="tel"
                            :placeholder="selectedWallet.lastFourDigitDebitAccount ? ('******' + selectedWallet.lastFourDigitDebitAccount) : ''"
                            maxlength="16"
                            autocomplete="cc-number"
                          />
                        </div>
                        <div v-if="errors.first('card number', 'page3NewCC')" class="validation-error">{{ errors.first("card number", "page3NewCC")}}</div>
                      </label>
                    </fieldset>
                    <fieldset class="bill-pay-slat__small-flex gds-fieldset">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Expiration</span>
                        <input
                          v-model="selectedWallet.expirationDate"
                          v-mask="'##/##'"
                          v-validate="'required|date_format:MM/yy|after:' + firstValidCard"
                          name="expiration date"
                          key="expiration date"
                          class="gds-input-field__input"
                          type="text"
                          placeholder="MM/YY"
                          autocomplete="cc-exp"
                        />
                        <div v-if="errors.first('expiration date', 'page3NewCC')" class="validation-error">{{ errors.first("expiration date", "page3NewCC")}}</div>
                      </label>
                    </fieldset>
                    <fieldset class="bill-pay-slat__small-flex gds-fieldset">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">CVV</span>
                        <input
                          v-model="tempCVV"
                          v-mask="'####'"
                          v-validate="'required|numeric|min:3|max:4'"
                          name="CVV"
                          key="CVV"
                          class="gds-input-field__input"
                          type="tel"
                          maxlength="4"
                          autocomplete="cc-csc"
                        />
                        <div v-if="errors.first('CVV', 'page3NewCC')" class="validation-error">{{ errors.first("CVV", "page3NewCC")}}</div>
                      </label>
                    </fieldset>
                  </div>
                  <fieldset class="gds-fieldset gds-space-stack-l">
                    <label class="gds-checkbox gds-checkbox-thin">
                      <input v-model="savePayment" type="checkbox" />
                      <span class="gds-checkbox__faux"></span>
                      <span class="gds-checkbox__label gds-display-00 gds-text-grey">Save this card for future use.</span>
                    </label>
                  </fieldset>
                  <fieldset v-if="savePayment" class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-l">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Payment method name</span>
                      <input
                        v-model="paymentName"
                        v-validate="'required'"
                        name="payment method name"
                        key="payment method name"
                        class="bill-pay-input-field__input gds-input-field__input"
                        type="text"
                        maxlength="50"
                      />
                      <div v-if="errors.first('payment method name', 'page3NewCC')" class="validation-error">{{ errors.first("payment method name", "page3NewCC")}}</div>
                    </label>
                  </fieldset>
                </form>
                <button @click="finishPage('page3NewCC', 4)" class="gds-button gds-compact">Next</button>
              </div>

              <!-- SLAT 3 NEW ATM INPUT -->
              <div v-if="selectedWallet && selectedWallet.isNew === 'atm'">
                <form data-vv-scope="page3NewATM" @submit.prevent>
                  <div class="bill-pay-slat__inner-row gds-space-stack-l">Please provide your card information.</div>
                  <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Name on Card</span>
                      <input
                        v-model="selectedWallet.debitName"
                        v-validate="'required|max:45'"
                        name="name on card"
                        key="name on card"
                        class="bill-pay-input-field__input gds-input-field__input"
                        type="text"
                        placeholder="Name"
                        maxlength="45"
                        autocomplete="cc-name"
                        ref="focuser3"
                      />
                      <div v-if="errors.first('name on card', 'page3NewATM')" class="validation-error">{{ errors.first("name on card", "page3NewATM")}}</div>
                    </label>
                  </fieldset>
                  <div class="bill-pay-slat__flex-container gds-flex-container gds-flex-container--top gds-space-stack-s">
                    <fieldset class="bill-pay-slat__card-number gds-fieldset">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Card Number</span>
                        <div class="gds-position--relative">
                          <span class="bill-pay__credit-card-prefix gds-input-field__prefix gds-text">
                            <svg class="bill-pay__icon gds-icon">
                              <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-credit-card" />
                            </svg>
                          </span>
                          <input
                            v-model="creditNumber"
                            v-mask="'################'"
                            v-validate="(selectedWallet.lastFourDigitDebitAccount ? '' : 'required|') + 'numeric|min:15|max:16'"
                            name="card number"
                            key="card number"
                            class="gds-input-field__input"
                            type="tel"
                            :placeholder="selectedWallet.lastFourDigitDebitAccount ? ('******' + selectedWallet.lastFourDigitDebitAccount) : ''"
                            maxlength="16"
                            autocomplete="cc-number"
                          />
                        </div>
                        <div v-if="errors.first('card number', 'page3NewATM')" class="validation-error">{{ errors.first("card number", "page3NewATM")}}</div>
                      </label>
                    </fieldset>
                  </div>
                  <fieldset class="gds-fieldset gds-space-stack-l">
                    <label class="gds-checkbox gds-checkbox-thin">
                      <input v-model="savePayment" type="checkbox" />
                      <span class="gds-checkbox__faux"></span>
                      <span class="gds-checkbox__label gds-display-00 gds-text-grey">Save this card for future use.</span>
                    </label>
                  </fieldset>
                  <fieldset v-if="savePayment" class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-l">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Payment method name</span>
                      <input
                        v-model="paymentName"
                        v-validate="'required'"
                        name="payment method name"
                        key="payment method name"
                        class="bill-pay-input-field__input gds-input-field__input"
                        type="text"
                        maxlength="50"
                      />
                      <div v-if="errors.first('payment method name', 'page3NewATM')" class="validation-error">{{ errors.first("payment method name", "page3NewATM")}}</div>
                    </label>
                  </fieldset>
                </form>
                <button @click="finishPage('page3NewATM', 4)" class="gds-button gds-compact">Next</button>
              </div>

              <!-- SLAT 3 NEW ACH INPUT -->
              <div v-if="selectedWallet && selectedWallet.isNew === 'ach'">
                <form data-vv-scope="page3NewACH" @submit.prevent>
                  <div class="bill-pay-slat__inner-row gds-space-stack-l">Please provide your bank account information.</div>
                  <div class="bill-pay-slat__inner-row gds-space-stack-l">
                    <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Account Type</span>
                        <select
                          v-model="selectedWallet.transType"
                          v-validate="'required'"
                          name="account type"
                          key="account type"
                          class="bill-pay-input-field__input gds-input-field bill-pay-input-field__select"
                          id="account-type"
                        >
                          <option value="SAVINGS">Savings</option>
                          <option value="CHECKING">Checking</option>
                          <option value="MONEY_MARKET">Money Market</option>
                        </select>
                        <div v-if="errors.first('account type', 'page3NewACH')" class="validation-error">{{ errors.first("account type", "page3NewACH")}}</div>
                      </label>
                    </fieldset>
                    <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Account Number</span>
                        <input
                          v-model="bankNumber"
                          v-mask="'################'"
                          v-validate="'required|numeric|min:5|max:16'"
                          name="account number"
                          key="account number"
                          class="bill-pay-input-field__input gds-input-field__input"
                          type="tel"
                          maxlength="16"
                          ref="focuser3"
                        />
                        <div v-if="errors.first('account number', 'page3NewACH')" class="validation-error">{{ errors.first("account number", "page3NewACH")}}</div>
                      </label>
                    </fieldset>
                    <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Routing Number</span>
                        <input
                          v-model="selectedWallet.debitRouting"
                          v-mask="'#########'"
                          v-validate="'required|numeric|min:9'"
                          name="routing number"
                          key="routing number"
                          class="bill-pay-input-field__input gds-input-field__input"
                          type="tel"
                          maxlength="9"
                        />
                        <div v-if="errors.first('routing number', 'page3NewACH')" class="validation-error">{{ errors.first("routing number", "page3NewACH")}}</div>
                      </label>
                    </fieldset>
                  </div>
                  <fieldset class="gds-fieldset gds-space-stack-l">
                    <label class="gds-checkbox gds-checkbox-thin">
                      <input v-model="savePayment" type="checkbox" />
                      <span class="gds-checkbox__faux"></span>
                      <span class="gds-checkbox__label gds-display-00 gds-text-grey">Save this bank account for future use.</span>
                    </label>
                  </fieldset>
                  <fieldset v-if="savePayment" class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-l">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Payment method name</span>
                      <input
                        v-model="paymentName"
                        v-validate="'required'"
                        name="payment method name"
                        key="payment method name"
                        class="bill-pay-input-field__input gds-input-field__input"
                        type="text"
                        maxlength="50"
                      />
                      <div v-if="errors.first('payment method name', 'page3NewACH')" class="validation-error">{{ errors.first("payment method name", "page3NewACH")}}</div>
                    </label>
                  </fieldset>
                </form>
                <button @click="finishPage('page3NewACH', 4)" class="gds-button gds-compact">Next</button>
              </div>

              <!-- SLAT 3 SHOW SAVED CC, LINK TO EDIT CARD INFO -->
              <div v-if="selectedWallet && !selectedWallet.isNew && (selectedWallet.paymentType === 'CC') && !editClicked">
                <form data-vv-scope="page3VerifyCC" @submit.prevent>
                  <div class="bill-pay-slat__inner-row gds-flex-container gds-flex-container--top gds-flex-container--space-between gds-flex-container--wrap">
                    <div class="bill-pay__existing-payment-method--left">
                      <div class="bill-pay__existing-payment-description gds-space-stack-l">For security, please enter the CVV associated with this card</div>
                      <fieldset class="bill-pay-slat__cvv gds-fieldset gds-space-stack-l">
                        <label class="gds-input-field">
                          <span class="gds-input-field__label">CVV</span>
                          <input
                            v-model="tempCVV"
                            v-mask="'####'"
                            v-validate="'required|numeric|min:3|max:4'"
                            name="CVV"
                            key="CVV"
                            class="bill-pay-slat__small-input gds-input-field__input"
                            type="tel"
                            maxlength="4"
                            autocomplete="cc-csc"
                            ref="focuser3"
                          />
                          <div v-if="errors.first('CVV', 'page3VerifyCC')" class="validation-error">{{ errors.first("CVV", "page3VerifyCC")}}</div>
                        </label>
                      </fieldset>
                    </div>

                    <div class="bill-pay__existing-payment-method--right gds-space-stack-l">
                      <div class="bill-pay__existing-payment-method-box gds-flex-container gds-flex-container--top">
                        <svg class="bill-pay__method-box-icon bill-pay__icon gds-icon">
                          <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-credit-card" />
                        </svg>
                        <div>
                          <div>{{selectedWallet.accountNickName}}</div>
                          <div>{{selectedWallet | walletSlug}}</div>
                        </div>
                      </div>
                      <div class="bill-pay__edit-card-details">
                        <button @click="editClicked = true; slatFocus(3)" class="clickable gds-button gds-text-button">
                          <svg class="bill-pay__edit-icon-inline gds-icon">
                            <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-edit" />
                          </svg>
                          Edit Card Details
                        </button>
                      </div>
                    </div>
                  </div>
                  <!--/end flex container -->
                </form>
                <button @click="finishPage('page3VerifyCC', 4)" class="gds-button gds-compact">Next</button>
              </div>

              <!-- SLAT 3 EDIT SCREEN FOR EXISTING CC  -->
              <div v-if="selectedWallet && !selectedWallet.isNew && selectedWallet.paymentType === 'CC' && editClicked">
                <form data-vv-scope="page3OldCC" @submit.prevent>
                  <div v-if="!selectedWallet.isExpired" class="bill-pay__existing-payment-description gds-space-stack-l">Edit the details for your saved card.</div>
                  <div v-if="selectedWallet.isExpired" class="form-message-box gds-space-stack-l">This payment method has expired, please update the payment details.</div>
                  <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Nickname</span>
                      <input
                        v-model="selectedWallet.accountNickName"
                        v-validate="'required|max:45'"
                        name="account nickname"
                        key="account nickname"
                        class="gds-input-field__input"
                        type="text"
                        placeholder="Account Nickname"
                        maxlength="45"
                      />
                    </label>
                  </fieldset>

                  <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Name on Card</span>
                      <input
                        v-model="selectedWallet.debitName"
                        v-validate="'required|max:45'"
                        name="name on card"
                        key="name on card"
                        class="gds-input-field__input"
                        type="text"
                        placeholder="Name"
                        maxlength="45"
                        autocomplete="cc-name"
                        ref="focuser3"
                      />
                      <div v-if="errors.first('name on card', 'page3OldCC')" class="validation-error">{{ errors.first("name on card", "page3OldCC")}}</div>
                    </label>
                  </fieldset>
                  <div class="bill-pay-slat__flex-container gds-flex-container gds-flex-container--top gds-space-stack-l">
                    <fieldset class="bill-pay-slat__card-number gds-fieldset">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Card Number</span>
                        <div class="bill-pay__credit-card-number-last-four gds-flex-container gds-flex-container--left">
                          <span class="bill-pay__credit-card-prefix gds-text">
                            <svg class="bill-pay__icon gds-icon">
                              <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-credit-card" />
                            </svg>
                          </span>
                          <span class="disabled-cardnumber">************{{selectedWallet.debitAccount}}</span>
                        </div>
                      </label>
                    </fieldset>
                    <fieldset class="bill-pay-slat__small-flex gds-fieldset">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Expiration Date</span>
                        <input
                          v-model="selectedWallet.expirationDate"
                          v-mask="'##/##'"
                          v-validate="'required|date_format:MM/yy|after:' + firstValidCard"
                          name="expiration date"
                          key="expiration date"
                          class="gds-input-field__input"
                          type="text"
                          placeholder="MM/YY"
                          autocomplete="cc-exp"
                        />
                        <div v-if="errors.first('expiration date', 'page3OldCC')" class="validation-error">{{ errors.first("expiration date", "page3OldCC")}}</div>
                      </label>
                    </fieldset>
                    <fieldset class="bill-pay-slat__small-flex gds-fieldset">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">CVV</span>
                        <input
                          v-model="tempCVV"
                          v-mask="'####'"
                          v-validate="'required|numeric|min:3|max:4'"
                          name="CVV"
                          key="CVV"
                          class="gds-input-field__input"
                          type="tel"
                          maxlength="4"
                          autocomplete="cc-csc"
                        />
                        <div v-if="errors.first('CVV', 'page3OldCC')" class="validation-error">{{ errors.first("CVV", "page3OldCC")}}</div>
                      </label>
                    </fieldset>
                  </div>
                </form>
                <div v-if="editCCError" class="form-message-box gds-space-stack-l">{{editCCError}}</div>
                <button @click="CancelOrBack()" class="bill-pay__paired-button gds-button gds-compact gds-secondary">Cancel Editing</button>
                <button
                  @click="saveOldCredit()"
                  :disabled="pendingSaveOldCredit"
                  :class="{ 'gds-loading': pendingSaveOldCredit }"
                  type="submit"
                  class="bill-pay__paired-button gds-button gds-compact"
                >
                  <span class="gds-button__text">Save and Continue</span>
                </button>
              </div>

              <!-- SLAT 3 EDIT SCREEN FOR EXISTING ATM  -->
              <div v-if="selectedWallet && !selectedWallet.isNew && selectedWallet.paymentType === 'ATM'">
                <form data-vv-scope="page3OldATM" @submit.prevent>
                  <div class="bill-pay__existing-payment-description gds-space-stack-l">Edit the details for your saved ATM card.</div>
                  <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Nickname</span>
                      <input
                        v-model="selectedWallet.accountNickName"
                        v-validate="'required|max:45'"
                        name="account nickname"
                        key="account nickname"
                        class="gds-input-field__input"
                        type="text"
                        placeholder="Account Nickname"
                        maxlength="45"
                      />
                    </label>
                  </fieldset>

                  <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                    <label class="gds-input-field">
                      <span class="gds-input-field__label">Name on Card</span>
                      <input
                        v-model="selectedWallet.debitName"
                        v-validate="'required|max:45'"
                        name="name on card"
                        key="name on card"
                        class="gds-input-field__input"
                        type="text"
                        placeholder="Name"
                        maxlength="45"
                        autocomplete="cc-name"
                        ref="focuser3"
                      />
                      <div v-if="errors.first('name on card', 'page3OldATM')" class="validation-error">{{ errors.first("name on card", "page3OldATM")}}</div>
                    </label>
                  </fieldset>
                  <div class="bill-pay-slat__flex-container gds-flex-container gds-flex-container--top gds-space-stack-l">
                    <fieldset class="bill-pay-slat__card-number gds-fieldset">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Card Number</span>
                        <div class="bill-pay__credit-card-number-last-four gds-flex-container gds-flex-container--left">
                          <span class="bill-pay__credit-card-prefix gds-text">
                            <svg class="bill-pay__icon gds-icon">
                              <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-credit-card" />
                            </svg>
                          </span>
                          <span class="disabled-cardnumber">************{{selectedWallet.debitAccount}}</span>
                        </div>
                      </label>
                    </fieldset>
                  </div>
                </form>
                <div v-if="editATMError" class="form-message-box gds-space-stack-l">{{editATMError}}</div>
                <button @click="gotoPage(4)" class="bill-pay__paired-button gds-button gds-compact gds-secondary">Cancel Editing</button>
                <button
                  @click="saveOldATM()"
                  :disabled="pendingSaveOldATM"
                  :class="{ 'gds-loading': pendingSaveOldATM }"
                  type="submit"
                  class="bill-pay__paired-button gds-button gds-compact"
                >
                  <span class="gds-button__text">Save and Continue</span>
                </button>
              </div>

              <!-- EDIT SCREEN FOR EXISTING ACH ACCOUNT  -->
              <div v-if="selectedWallet && !selectedWallet.isNew && selectedWallet.paymentType === 'ACH'">
                <form data-vv-scope="page3OldACH" @submit.prevent>
                  <div
                    v-if="!selectedWallet.isDebitAccountUnauthorized"
                    class="bill-pay__existing-payment-description gds-space-stack-l"
                  >Edit the details for your saved bank account.</div>
                  <div v-if="selectedWallet.isDebitAccountUnauthorized" class="form-message-box gds-space-stack-l">Please re-enter the information for this bank account.</div>
                  <div class="bill-pay-slat__inner-row gds-space-stack-l">
                    <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Nickname</span>
                        <input
                          v-model="selectedWallet.accountNickName"
                          v-validate="'required|max:45'"
                          name="account nickname"
                          key="account nickname"
                          class="gds-input-field__input"
                          type="text"
                          placeholder="Account Nickname"
                          maxlength="45"
                        />
                      </label>
                    </fieldset>
                    <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Account Type</span>
                        <select
                          v-model="selectedWallet.transType"
                          v-validate="'required'"
                          name="account type"
                          key="account type"
                          class="bill-pay-input-field__select gds-input-field"
                          id="account-type"
                        >
                          <option value="SAVINGS">Savings</option>
                          <option value="CHECKING">Checking</option>
                          <option value="MONEY_MARKET">Money Market</option>
                        </select>
                        <div v-if="errors.first('account type', 'page3OldACH')" class="validation-error">{{ errors.first("account type", "page3OldACH")}}</div>
                      </label>
                    </fieldset>
                    <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Account Number</span>
                        <input
                          v-model="bankNumber"
                          v-mask="'################'"
                          v-validate="'required|numeric|min:5|max:16'"
                          name="account number"
                          key="account number"
                          :placeholder="selectedWallet.debitAccount ? '******' + selectedWallet.debitAccount : ''"
                          class="gds-input-field__input"
                          type="text"
                          maxlength="16"
                          ref="focuser3"
                        />
                        <div v-if="errors.first('account number', 'page3OldACH')" class="validation-error">{{ errors.first("account number", "page3OldACH")}}</div>
                      </label>
                    </fieldset>
                    <fieldset class="bill-pay__fieldset-wrap gds-fieldset gds-space-stack-s">
                      <label class="gds-input-field">
                        <span class="gds-input-field__label">Routing Number</span>
                        <input
                          v-model="selectedWallet.debitRouting"
                          v-mask="'#########'"
                          v-validate="'required|numeric|min:9'"
                          name="routing number"
                          key="routing number"
                          class="gds-input-field__input"
                          type="text"
                          maxlength="9"
                        />
                        <div v-if="errors.first('routing number', 'page3OldACH')" class="validation-error">{{ errors.first("routing number", "page3OldACH")}}</div>
                      </label>
                    </fieldset>
                  </div>
                </form>
                <div v-if="editACHError" class="form-message-box gds-space-stack-l">{{editACHError}}</div>
                <button @click="CancelOrBack()" class="bill-pay__paired-button gds-button gds-compact gds-secondary">Cancel Editing</button>
                <button
                  @click="saveOldACH()"
                  :disabled="pendingSaveOldACH"
                  :class="{ 'gds-loading': pendingSaveOldACH }"
                  type="submit"
                  class="bill-pay__paired-button gds-button gds-compact"
                >
                  <span class="gds-button__text">Save and Continue</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- SLAT 4 -->
      <section id="pay-slat-4" class="review-submit bill-pay-section">
        <div class="bill-pay-slat gds-slat gds-slat--active">
          <div class="bill-pay-slat__header gds-slat__header">
            <div class="bill-pay-slat__header-left gds-flex-container">
              <div class="bill-pay-slat__number-with-circle">
                <span>4</span>
              </div>
              <div class="gds-slat__title-wrapper">
                <div class="bill-pay-slat__title gds-slat__title">Review &amp; Submit</div>
              </div>
            </div>
            <div class="bill-pay-slat__header-right gds-flex-container">
              <div v-if="currentPage < 4" class="bill-pay-slat__lock-icon">
                <svg class="gds-icon">
                  <use xlink:href="/wp-content/themes/gmptwentynineteen/assets/symbol-defs.svg#gds-icon-lock" />
                </svg>
              </div>
            </div>
          </div>
          <div v-if="currentPage === 4" class="bill-pay-slat__contents">
            <div class="bill-pay-slat__contents-inner">
              <div class="bill-pay-slat__inner-row gds-space-stack-l">
                <div class="gds-display-00 gds-space-stack-s">GMP Account</div>
                <div class="gds-body-small gds-space-stack-l">
                  #{{currentAccount.accountNumber}}
                  <br />
                  {{currentAccount.address | addressFormat({ separator: ", " })}}
                </div>
                <div class="gds-display-00 gds-space-stack-s">Amount</div>
                <div class="gds-body-small gds-space-stack-l">{{paymentAmount | currency}}</div>
                <div class="bill-pay__flex-container--left gds-flex-container gds-flex-container--top">
                  <div v-if="selectedWallet" class="gds-space-inline-xl">
                    <div class="gds-display-00 gds-space-stack-s">Payment Method</div>
                    <div class="gds-body-small gds-space-stack-l">{{(selectedWallet.isNew && this.savePayment) ? this.paymentName : selectedWallet.accountNickName}}</div>
                    <div class="gds-body-small gds-space-stack-l">{{selectedWallet | walletSlug}}</div>
                  </div>
                  <div class="gds-space-inline-m">
                    <div class="gds-display-00 gds-space-stack-s">Payment Date</div>
                    <div class="gds-body-small gds-space-stack-l">{{ todayDate | dateFull}}</div>
                  </div>
                </div>
                <div v-if="makePaymentError" class="form-message-box gds-space-stack-l">{{makePaymentError}}</div>
                <button @click="submitPayment()" :disabled="pendingSubmitPayment" :class="{ 'gds-loading': pendingSubmitPayment }" type="submit" class="gds-button gds-compact">
                  <span class="gds-button__text">Submit Payment</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
  <!-- #main -->
</template>

<script>
import { ErrorBag } from "vee-validate";
import { parseISO, format, addMonths } from "date-fns";

import GMPAPI from "../../services/gmpapi";
import { GetCardType, ToFullExpirationDate } from "../../utilities";
import WalletSelector from "../generic/WalletSelector";
import { DumpError } from "../../utilities";

export default {
  name: "BillPayFlowLoggedIn",
  data() {
    return {
      userWallets: [],
      selectedWallet: undefined,
      savePayment: false,
      paymentName: "",
      editClicked: false,
      isKeyboarding: false,

      paymentAmount: 0,

      // Keep these separate since they use the same field
      creditNumber: "",
      bankNumber: "",

      tempCVV: "",
      tokenizedCard: undefined,

      todayDate: new Date(),
      firstValidCard: format(addMonths(new Date(), -1), "MM/yy"),

      paymentMeta: {
        minACH: 1,
        maxACH: 9999999.99,
        minCC: 10,
        maxCC: 2500
      },
      tooManyCCPayments: false,

      editACHError: undefined,
      editATMError: undefined,
      editCCError: undefined,
      makePaymentError: undefined,

      pendingSubmitPayment: false,
      pendingSaveOldCredit: false,
      pendingSaveOldATM: false,
      pendingSaveOldACH: false,

      loadState: undefined,
    };
  },
  components: {
    WalletSelector,
  },
  computed: {
    currentAccount() {
      return this.$store.state.user.currentAccount;
    },
    currentPage() {
      return Number(this.$route.params.page);
    },
    pastDue() {
      return new Date(this.currentAccount.amountDueDate) < new Date();
    },
    amountDue() {
      if (this.currentAccount.amountDueMinusPending !== undefined) {
        return this.currentAccount.amountDueMinusPending;
      } else {
        return this.currentAccount.amountDue;
      }
    },
    pendingPayments() {
      return this.currentAccount.pendingPaymentsCount && this.currentAccount.pendingPaymentsCount > 0;
    },
    recurringAuth() {
      if (this.currentAccount && this.currentAccount.mailingAddress && this.currentAccount.mailingAddress.zip) {
        return this.currentAccount.mailingAddress.zip.substr(0, 5);
      } else {
        return "00000";
      }
    },
  },
  created() {
    window.addEventListener("keydown", (e) => {
      if (this.currentPage === 2) {
        this.isKeyboarding = true;
      } else {
        this.isKeyboarding = false;
      }
    });
  },
  async mounted() {
    if (this.amountDue > 0) {
      this.paymentAmount = this.amountDue;
    } else {
      this.paymentAmount = 0;
    }
    // Kick off async fetches
    try {
      this.loadState = undefined;
      await Promise.all([
        this.fetchPaymentMeta(),
        this.fetchUserWallets(),
        this.fetchCCPayments(),
      ]);
      this.loadState = "complete";
    } catch (err) {
      DumpError("Billpay loggedin load error", err);
      this.loadState = "error";
    }
  },
  methods: {
    /** Go to a page number without validation */
    gotoPage(page) {
      this.isKeyboarding = false;
      this.$router.push({ path: "/pay/" + page });
      this.slatFocus(page);
    },
    slatFocus(page) {
      // No good way to wait for the route to finish loading DOM, so do a flat timeout
      setTimeout(() => {
        const focuser = this.$refs["focuser" + page];
        if (focuser && focuser.$el) {
          if (focuser.$el.focus) focuser.$el.focus();
        } else if (focuser && Array.isArray(focuser) && focuser.length >= 1) {
          // Page 2 has array of wallets
          if (focuser[0].focus) focuser[0].focus();
        } else if (focuser) {
          if (focuser.focus) focuser.focus();
        }
      }, 100);
    },
    /** Validate the current form, and if successful, go to a page number */
    async finishPage(pageName, nextPage) {
      if (await this.$validator.validateAll(pageName)) {
        switch (pageName) {
          case "page1":
            // If under $10 (or too many CC payments), select an ACH wallet
            if (
              this.selectedWallet &&
              (this.selectedWallet.paymentType === "CC" || this.selectedWallet.paymentType === "ATM") &&
              (this.tooManyCCPayments ||
                this.paymentAmount < this.paymentMeta.minCC ||
                this.paymentAmount > this.paymentMeta.maxCC)
            ) {
              if (this.userWallets) {
                const firstACH = this.userWallets.find(
                  item => item.paymentType === "ACH"
                );
                if (firstACH) {
                  this.selectedWallet = firstACH;
                }
              }
            }
            break;
          case "page3VerifyCC":
            this.selectedWallet.cvv = this.tempCVV;
            this.tempCVV = "";
            break;
          case "page3NewCC":
            // First make sure the nickname is unique
            if (this.selectedNicknameIsDuplicate()) {
              // Duplicate nickname, bail!
              // Make sure we have a fresh ErrorBag for handling special cases
              if (!this.errors) this.errors = new ErrorBag();
              this.errors.add({ field: "payment method name", msg: "The nickname entered is already in use by another payment method. Please change the nickname.", scope: "page3NewCC" });
              return;
            }
            // If they've entered or altered the CC number, validate and tokenize
            if (this.creditNumber) {
              const cardType = GetCardType(this.creditNumber);
              if (!cardType) {
                // Unknown card type, bail!
                // Make sure we have a fresh ErrorBag for handling special cases
                if (!this.errors) this.errors = new ErrorBag();
                this.errors.add({ field: "card number", msg: "Not a valid card number", scope: "page3NewCC" });
                return;
              }
              this.selectedWallet.cardType = cardType;
              try {
                this.tokenizedCard = await GMPAPI.TokenizeCreditCard(
                  this.paymentMeta.tokenUrl,
                  this.creditNumber
                );
                this.selectedWallet.lastFourDigitDebitAccount = this.creditNumber.slice(-4);
                this.creditNumber = "";
              } catch (err) {
                // Bad response from tokenizer
                DumpError("New CC next tokenize error", err);
                // Make sure we have a fresh ErrorBag for handling special cases
                if (!this.errors) this.errors = new ErrorBag();
                this.errors.add({ field: "card number", msg: "Not a valid card", scope: "page3NewCC" });
                return;
              }
              this.selectedWallet.cvv = this.tempCVV;
              this.tempCVV = "";
            }
            break;
          case "page3NewATM":
            // First make sure the nickname is unique
            if (this.selectedNicknameIsDuplicate()) {
              // Duplicate nickname, bail!
              // Make sure we have a fresh ErrorBag for handling special cases
              if (!this.errors) this.errors = new ErrorBag();
              this.errors.add({ field: "payment method name", msg: "The nickname entered is already in use by another payment method. Please change the nickname.", scope: "page3NewATM" });
              return;
            }
            // If they've entered or altered the CC number, validate and tokenize
            if (this.creditNumber) {
              const cardType = GetCardType(this.creditNumber);
              if (!cardType) {
                // Unknown card type, bail!
                // Make sure we have a fresh ErrorBag for handling special cases
                if (!this.errors) this.errors = new ErrorBag();
                this.errors.add({ field: "card number", msg: "Not a valid card number", scope: "page3NewATM" });
                return;
              }
              this.selectedWallet.cardType = cardType;
              try {
                this.tokenizedCard = await GMPAPI.TokenizeCreditCard(
                  this.paymentMeta.tokenUrl,
                  this.creditNumber
                );
                this.selectedWallet.lastFourDigitDebitAccount = this.creditNumber.slice(-4);
                this.creditNumber = "";
              } catch (err) {
                // Bad response from tokenizer
                DumpError("New CC next tokenize error", err);
                // Make sure we have a fresh ErrorBag for handling special cases
                if (!this.errors) this.errors = new ErrorBag();
                this.errors.add({ field: "card number", msg: "Not a valid card", scope: "page3NewATM" });
                return;
              }
            }
            break;
          case "page3NewACH":
            // First make sure the nickname is unique
            if (this.selectedNicknameIsDuplicate()) {
              // Duplicate nickname, bail!
              // Make sure we have a fresh ErrorBag for handling special cases
              if (!this.errors) this.errors = new ErrorBag();
              this.errors.add({ field: "payment method name", msg: "The nickname entered is already in use by another payment method. Please change the nickname.", scope: "page3NewACH" });
              return;
            }
        }
        // If old ACH or ATM, skip step 3
        if (
          nextPage === 3 &&
          (this.selectedWallet.paymentType === "ACH" || this.selectedWallet.paymentType === "ATM") &&
          !this.selectedWallet.isNew && !this.selectedWallet.isExpired && !this.selectedWallet.isDebitAccountUnauthorized
        ) {
          nextPage = 4;
        }
        if (this.selectedWallet.isExpired || this.selectedWallet.isDebitAccountUnauthorized) {
          this.editClicked = true;
          // Clear bank numbers
          if (this.selectedWallet.isDebitAccountUnauthorized && this.selectedWallet.debitRouting) {
            this.selectedWallet.debitAccount = undefined;
            this.selectedWallet.debitRouting = undefined;
          }
        } else {
          this.editClicked = false;
        }
        this.makePaymentError = undefined;
        this.gotoPage(nextPage);
      }
    },
    CancelOrBack() {
      // If this is an invalid payment method, go back to page 2 on cancel
      if (this.selectedWallet.isExpired || this.selectedWallet.isDebitAccountUnauthorized) {
        this.gotoPage(2);
      } else {
        if (this.selectedWallet.paymentType === 'ACH') {
          // Bank cancel goes to page 4
          this.gotoPage(4);
        } else {
          // Credit cancel goes to same page
          this.editClicked = false;
          this.slatFocus(3);
        }
      }
    },
    selectedNicknameIsDuplicate() {
      const matcher = this.paymentName.toLowerCase();
      for (const wallet of this.userWallets) {
        if (wallet.accountNickName.toLowerCase() === matcher) return true;
      }
      return false;
    },
    async fetchUserWallets() {
      const wallets = await GMPAPI.GetWallets(this.currentAccount.accountNumber);
      // Add "new" options
      wallets.push({
        isNew: "credit",
        paymentType: "CC",
        accountNickName: "New Credit / Debit Card",
        transType: "CARD"
      });
      wallets.push({
        isNew: "atm",
        paymentType: "ATM",
        accountNickName: "New ATM Card",
        transType: "CARD"
      });
      wallets.push({
        isNew: "ach",
        paymentType: "ACH",
        accountNickName: "New Bank Account",
        transType: "CHECKING"
      });
      this.userWallets = wallets;
      // Initialize selected to first
      this.selectedWallet = wallets[0];
    },
    async fetchPaymentMeta() {
      const meta = await GMPAPI.GetPaymentMeta();
      this.paymentMeta = meta;
    },
    async fetchCCPayments() {
      let payments = [];
      payments = await GMPAPI.GetBillingHistory(this.currentAccount.accountNumber, 30);
      const ccpays = payments.filter(item => (item.paymentType === "CC" || item.paymentType === "ATM") && (item.status === "PROCESSED" || item.status === "PENDING"));
      if (ccpays.length >= 2) {
        // 2 or more cc/atm payments this month, disable CC pay
        this.tooManyCCPayments = true;
      } else {
        this.tooManyCCPayments = false;
      }
    },
    async saveOldCredit() {
      this.editCCError = undefined;
      if (await this.$validator.validateAll("page3OldCC")) {
        // If they've entered or altered the CC number, validate and tokenize
        // Currently this should never run since it's disabled
        if (this.creditNumber) {
          const cardType = GetCardType(this.creditNumber);
          if (!cardType) {
            // Unknown card type, bail!
            // Make sure we have a fresh ErrorBag for handling special cases
            if (!this.errors) this.errors = new ErrorBag();
            this.errors.add({ field: "card number", msg: "Not a valid card number", scope: "page3OldCC" });
            return;
          }
          this.selectedWallet.cardType = cardType;
          try {
            this.tokenizedCard = await GMPAPI.TokenizeCreditCard(
              this.paymentMeta.tokenUrl,
              this.creditNumber
            );
            this.selectedWallet.lastFourDigitDebitAccount = this.creditNumber.slice(-4);
            this.creditNumber = "";
          } catch (err) {
            // Bad response from tokenizer
            DumpError("Save credit tokenize error", err);
            // Make sure we have a fresh ErrorBag for handling special cases
            if (!this.errors) this.errors = new ErrorBag();
            this.errors.add({ field: "card number", msg: "Not a valid card", scope: "page3OldCC" });
            return;
          }
        }
        this.selectedWallet.cvv = this.tempCVV;
        this.tempCVV = "";
        this.pendingSaveOldCredit = true;
        try {
          const wallet = this.selectedWallet;
          const walletRecord = {
            accountNickName: wallet.accountNickName,
            paymentType: wallet.paymentType,
            debitAccount: this.tokenizedCard || ("*" + wallet.debitAccount),
            expirationDate: ToFullExpirationDate(wallet.expirationDate),
            cvv: wallet.cvv,
            transType: wallet.transType,
            creditAccount: this.currentAccount.accountNumber,
            cardType: wallet.cardType,
            accountNickName: wallet.accountNickName,
            // Populate all location fields from account, should be required but ignored
            debitZip: this.currentAccount.address.zip,
            creditZip: this.currentAccount.address.zip,
            debitState: this.currentAccount.address.state,
            creditState: this.currentAccount.address.state,
            debitCity: this.currentAccount.address.city,
            creditCity: this.currentAccount.address.city,
            debitAddress1: this.currentAccount.address.street1
          };
          await GMPAPI.UpdateWallet(
            this.currentAccount.accountNumber,
            walletRecord,
            wallet.walletId
          );
          this.editClicked = false;
          this.gotoPage(4);
        } catch (err) {
          DumpError("Save credit error", err);
          if (err.response && err.response.data) {
            if (err.response.data.message) {
              console.error(err.response.data.message);
            }
            const code = err.response.data.code;
            // Make sure we have a fresh ErrorBag for handling special cases
            if (!this.errors) this.errors = new ErrorBag();
            // Take specific actions on certain error codes here
            switch (code) {
              case 1001:
                this.errors.add({ field: "card number", msg: "That card was declined.", scope: "page3OldCC" });
                break;
              // CVV
              case 18033:
                this.errors.add({ field: "CVV", msg: "The CVV was incorrect.", scope: "page3OldCC" });
                break;
              // Expiration date
              case 18015:
              case 18016:
                this.errors.add({ field: "expiration date", msg: "The expiration date was incorrect.", scope: "page3OldCC" });
                break;
              default:
                // Not an error we expected, so show the big red generic message
                this.editCCError = "There was an unexpected error saving your card.";
            }
          } else {
            this.editCCError = "There was an unexpected error saving your card.";
          }
        } finally {
          this.pendingSaveOldCredit = false;
        }
      }
    },
    async saveOldATM() {
      this.editATMError = undefined;
      if (await this.$validator.validateAll("page3OldATM")) {
        // If they've entered or altered the CC number, validate and tokenize
        // Currently this should never run since it's disabled
        if (this.creditNumber) {
          const cardType = GetCardType(this.creditNumber);
          if (!cardType) {
            // Unknown card type, bail!
            // Make sure we have a fresh ErrorBag for handling special cases
            if (!this.errors) this.errors = new ErrorBag();
            this.errors.add({ field: "card number", msg: "Not a valid card number", scope: "page3OldATM" });
            return;
          }
          this.selectedWallet.cardType = cardType;
          try {
            this.tokenizedCard = await GMPAPI.TokenizeCreditCard(
              this.paymentMeta.tokenUrl,
              this.creditNumber
            );
            this.selectedWallet.lastFourDigitDebitAccount = this.creditNumber.slice(-4);
            this.creditNumber = "";
          } catch (err) {
            // Bad response from tokenizer
            DumpError("Save credit tokenize error", err);
            // Make sure we have a fresh ErrorBag for handling special cases
            if (!this.errors) this.errors = new ErrorBag();
            this.errors.add({ field: "card number", msg: "Not a valid card", scope: "page3OldATM" });
            return;
          }
        }
        this.pendingSaveOldATM = true;
        try {
          const wallet = this.selectedWallet;
          const walletRecord = {
            accountNickName: wallet.accountNickName,
            paymentType: wallet.paymentType,
            debitAccount: this.tokenizedCard || ("*" + wallet.debitAccount),
            transType: wallet.transType,
            creditAccount: this.currentAccount.accountNumber,
            cardType: wallet.cardType,
            accountNickName: wallet.accountNickName,
            // Populate all location fields from account, should be required but ignored
            debitZip: this.currentAccount.address.zip,
            creditZip: this.currentAccount.address.zip,
            debitState: this.currentAccount.address.state,
            creditState: this.currentAccount.address.state,
            debitCity: this.currentAccount.address.city,
            creditCity: this.currentAccount.address.city,
            debitAddress1: this.currentAccount.address.street1
          };
          await GMPAPI.UpdateWallet(
            this.currentAccount.accountNumber,
            walletRecord,
            wallet.walletId
          );
          this.editClicked = false;
          this.gotoPage(4);
        } catch (err) {
          DumpError("Save atm error", err);
          if (err.response && err.response.data) {
            if (err.response.data.message) {
              console.error(err.response.data.message);
            }
            const code = err.response.data.code;
            // Make sure we have a fresh ErrorBag for handling special cases
            if (!this.errors) this.errors = new ErrorBag();
            // Take specific actions on certain error codes here
            switch (code) {
              case 1001:
                this.errors.add({ field: "card number", msg: "That card was declined.", scope: "page3OldATM" });
                break;
              default:
                // Not an error we expected, so show the big red generic message
                this.editATMError = "There was an unexpected error saving your card.";
            }
          } else {
            this.editATMError = "There was an unexpected error saving your card.";
          }
        } finally {
          this.pendingSaveOldATM = false;
        }
      }
    },
    async saveOldACH() {
      this.editACHError = undefined;
      if (await this.$validator.validateAll("page3OldACH")) {
        this.pendingSaveOldACH = true;
        try {
          const wallet = this.selectedWallet;
          const walletRecord = {
            accountNickName: wallet.accountNickName,
            paymentType: wallet.paymentType,
            debitRouting: wallet.debitRouting,
            debitAccount: wallet.debitAccount,
            transType: wallet.transType,
            creditAccount: this.currentAccount.accountNumber,
            // Populate all location fields from account, should be required but ignored
            debitZip: this.currentAccount.address.zip,
            creditZip: this.currentAccount.address.zip,
            debitState: this.currentAccount.address.state,
            creditState: this.currentAccount.address.state,
            debitCity: this.currentAccount.address.city,
            creditCity: this.currentAccount.address.city,
            debitAddress1: this.currentAccount.address.street1
          };
          await GMPAPI.UpdateWallet(
            this.currentAccount.accountNumber,
            walletRecord,
            wallet.walletId
          );
          this.gotoPage(4);
        } catch (err) {
          DumpError("Save ACH error", err);
          if (err.response && err.response.data) {
            if (err.response.data.message) {
              console.error(err.response.data.message);
            }
            const code = err.response.data.code;
            // Make sure we have a fresh ErrorBag for handling special cases
            if (!this.errors) this.errors = new ErrorBag();
            // Take specific actions on certain error codes here
            switch (code) {
              // Account number
              case 18055:
                this.errors.add({ field: "account number", msg: "The account number was incorrect.", scope: "page3OldACH" });
                break;
              // Routing number
              case 18056:
                this.errors.add({ field: "routing number", msg: "The routing number was incorrect.", scope: "page3OldACH" });
                break;
              default:
                // Not an error we expected, so show the big red generic message
                this.editACHError = "There was an unexpected error saving your bank account.";
            }
          } else {
            this.editACHError = "There was an unexpected error saving your bank account.";
          }
        } finally {
          // Either way, turn off the loading indicator
          this.pendingSaveOldACH = false;
        }
      }
    },
    async submitPayment() {
      this.makePaymentError = undefined;
      this.pendingSubmitPayment = true;
      try {
        const accountDetails = this.$store.state.user.userinfo;
        const wallet = this.selectedWallet;
        if (this.savePayment && wallet.isNew) {
          const walletRecord = {
            paymentType: wallet.paymentType,
            debitRouting: wallet.debitRouting,
            debitAccount: (wallet.isNew === "credit" || wallet.isNew === "atm") ? this.tokenizedCard : this.bankNumber,
            expirationDate: wallet.expirationDate ? ToFullExpirationDate(wallet.expirationDate) : undefined,
            cvv: wallet.cvv || "000",
            transType: wallet.transType,
            creditAccount: this.currentAccount.accountNumber,
            cardType: wallet.cardType,
            accountNickName: wallet.isNew
              ? this.paymentName
              : this.accountNickName,
            // Populate all location fields from account, should be required but ignored
            debitZip: this.currentAccount.address.zip,
            creditZip: this.currentAccount.address.zip,
            debitState: this.currentAccount.address.state,
            creditState: this.currentAccount.address.state,
            debitCity: this.currentAccount.address.city,
            creditCity: this.currentAccount.address.city,
            debitAddress1: this.currentAccount.address.street1
          };
          await GMPAPI.CreateWallet(
            this.currentAccount.accountNumber,
            walletRecord
          );
        }
        const paymentRecord = {
          customerInformation: {
            // Payment
            // If no debitName (saved payment), fill in account name to satisfy GMP api
            debitName: wallet.debitName || accountDetails.fullName,
            // Account
            creditFirstname: accountDetails.givenName,
            creditLastname: accountDetails.surname,
            phone: accountDetails.phone,
            creditAccount: this.currentAccount.accountNumber,
            // Populate all location fields from account, should be required but ignored
            debitZip: this.currentAccount.address.zip,
            creditZip: this.currentAccount.address.zip,
            debitState: this.currentAccount.address.state,
            creditState: this.currentAccount.address.state,
            debitCity: this.currentAccount.address.city,
            creditCity: this.currentAccount.address.city,
            debitAddress1: this.currentAccount.address.street1
          },
          debitInformation: {
            paymentType: wallet.paymentType,
            debitRouting: wallet.debitRouting,
            // If new credit/atm, tokenized. If new bank, bankNumber. If old, * + last four digits
            debitAccount: wallet.isNew ? ((wallet.isNew === "credit" || wallet.isNew === "atm") ? this.tokenizedCard : this.bankNumber) : "*" + wallet.debitAccount,
            // Note, speedpay docs say they want MM/YYYY but GMP api expects full ISO
            // Only convert if we have the shorthand input date, otherwise leave alone
            expDate:
              wallet.expirationDate && wallet.expirationDate.length < 8
                ? ToFullExpirationDate(wallet.expirationDate)
                : wallet.expirationDate,
            cvv: wallet.cvv || "000",
            // NOTE alternate casing of transType
            transtype: wallet.transType,
            // If CC can process as ATM, do it
            skipCcOverAtm: false,
            // Pull existing wallet if not new
            retrieveWallet: !!wallet.walletId
          },
          paymentInformation: {
            emailAddress: accountDetails.email,
            paymentAmount: this.paymentAmount
          }
        };
        const paymentResult = await GMPAPI.MakePayment(this.currentAccount.accountNumber, paymentRecord);
        this.$store.commit("setPaymentAmount", this.paymentAmount);
        this.$store.commit("setConfirmationNumber", paymentResult.paymentId);
        // Update account data (pending payments)
        await this.$store.dispatch("FetchUserInfo");
        this.$router.push({ path: "/complete" });
      } catch (err) {
        DumpError("Submit payment loggedin error", err);
        if (err.response && err.response.data) {
          if (err.response.data.message) {
            console.error(err.response.data.message);
          }
          const code = err.response.data.code;
          // Make sure we have a fresh ErrorBag for handling special cases
          if (!this.errors) this.errors = new ErrorBag();
          // Take specific actions on certain error codes here
          switch (code) {
            // CVV
            case 1006:
              this.errors.add({ field: "CVV", msg: "The CVV was incorrect.", scope: "page3NewCC" });
              this.errors.add({ field: "CVV", msg: "The CVV was incorrect.", scope: "page3OldCC" });
              this.gotoPage(3);
              break;
            // Expiration date
            case 1012:
              if (this.selectedWallet.isNew) {
                this.errors.add({ field: "expiration date", msg: "The expiration date was incorrect.", scope: "page3NewCC" });
                this.gotoPage(3);
              } else {
                this.editClicked = true;
                this.errors.add({ field: "expiration date", msg: "The expiration date was incorrect.", scope: "page3OldCC" });
                this.gotoPage(3);
              }
              break;
            // CC Mod 10
            case 3342:
            case 18077:
              this.errors.add({ field: "card number", msg: "The card number was incorrect.", scope: "page3NewCC" });
              this.errors.add({ field: "card number", msg: "The card number was incorrect.", scope: "page3NewATM" });
              this.gotoPage(3);
              break;
            // Routing number
            case 2517:
            case 4330:
              this.errors.add({ field: "routing number", msg: "The routing number was incorrect.", scope: "page3NewACH" });
              this.gotoPage(3);
              break;
            // Account number
            case 2518:
              this.errors.add({ field: "account number", msg: "The account number was incorrect.", scope: "page3NewACH" });
              this.gotoPage(3);
              break;

            // The following are non-fatal errors, but don't redirect to a particular field

            // Declined
            case 1001:
            // ATM Declined
            case 9002:
              this.makePaymentError = "The card was declined.";
              break;
            // Duplicate
            case 3307:
              this.makePaymentError = "This payment appears to be a duplicate! You recently paid the exact same amount on this account. To prevent you from accidentally paying twice, it has NOT been processed. If you’d still like to make a payment on this account, change the amount and submit it again.";
              break;
            // More than 2 payments / month
            case 3803:
              this.makePaymentError = "GMP allows two credit card payments per account per month. If you would like to make additional payments on your account this month, please use your checking account.";
              break;

            // Any other error code gets redirected to the "fatal" error page
            default:
              this.$router.push({ path: "/error/" + code });
              break;
          }
        } else {
          this.$router.push({ path: "/error/" + code });
        }
      } finally {
        // Either way, turn off the loading indicator
        this.pendingSubmitPayment = false;
      }
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>